/**
 * Date utility functions for consistent date formatting across the application.
 * These functions help prevent timezone-related issues when displaying dates.
 */

/**
 * Formats a date string to display without timezone conversion.
 * This prevents dates from shifting due to timezone differences.
 * 
 * Use this function when you want to display a date exactly as it appears
 * in the data, without any timezone conversion (e.g., action dates, record dates).
 * 
 * @param dateString - ISO date string from the backend (e.g., "2024-01-15T10:00:00Z")
 * @returns Formatted date string in MM/DD/YYYY format (locale-specific)
 * 
 * @example
 * ```typescript
 * // Input: "2024-01-15T10:00:00Z" (UTC)
 * // Output: "1/15/2024" (regardless of user's timezone)
 * const formattedDate = formatDateWithoutTimezone("2024-01-15T10:00:00Z");
 * ```
 */
export const formatDateWithoutTimezone = (dateString: string): string => {
  try {
    // Parse the date string and extract just the date part
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${dateString}`);
    }
    
    // Create a new date using the extracted UTC date components to avoid timezone conversion
    const localDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    
    return localDate.toLocaleDateString();
  } catch (error) {
    console.warn('Error formatting date without timezone:', error);
    return dateString; // Fallback to original string
  }
};

/**
 * Formats a date string with timezone conversion (standard behavior).
 * Use this when you want the date to be displayed in the user's local timezone.
 * 
 * This is useful for timestamps, created dates, modified dates, etc.
 * 
 * @param dateString - ISO date string from the backend
 * @returns Formatted date string in the user's local timezone
 * 
 * @example
 * ```typescript
 * // Input: "2024-01-15T10:00:00Z" (UTC)
 * // Output: "1/15/2024" or "1/14/2024" (depending on user's timezone)
 * const formattedDate = formatDateWithTimezone("2024-01-15T10:00:00Z");
 * ```
 */
export const formatDateWithTimezone = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${dateString}`);
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    console.warn('Error formatting date with timezone:', error);
    return dateString; // Fallback to original string
  }
};

/**
 * Formats a date string with custom locale and options.
 * Provides timezone-safe formatting with full control over the output format.
 * 
 * @param dateString - ISO date string from the backend
 * @param locale - Locale string (e.g., "en-US", "en-GB")
 * @param options - Intl.DateTimeFormatOptions for custom formatting
 * @param useTimezone - Whether to apply timezone conversion (default: false)
 * @returns Formatted date string according to specified options
 * 
 * @example
 * ```typescript
 * // Custom formatting without timezone conversion
 * const formatted = formatDateCustom("2024-01-15T10:00:00Z", "en-US", {
 *   month: "short",
 *   day: "numeric", 
 *   year: "numeric"
 * });
 * // Output: "Jan 15, 2024"
 * ```
 */
export const formatDateCustom = (
  dateString: string,
  locale: string = "en-US",
  options: Intl.DateTimeFormatOptions = {},
  useTimezone: boolean = false
): string => {
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${dateString}`);
    }
    
    if (useTimezone) {
      return date.toLocaleDateString(locale, options);
    } else {
      // Use UTC components to avoid timezone conversion
      const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      return utcDate.toLocaleDateString(locale, options);
    }
  } catch (error) {
    console.warn('Error formatting date with custom options:', error);
    return dateString; // Fallback to original string
  }
};
